/**
 * Запасний рушій озвучки через Web Speech API.
 *
 * Збережено без змін у поведінці: українська мова, вибір системного голосу,
 * швидкість, послідовна озвучка слів і підписка на voiceschanged.
 */

import { stripStress } from './stress.js'

const synth = typeof window !== 'undefined' ? window.speechSynthesis : null

export const isSupported = () => Boolean(synth) && typeof window.SpeechSynthesisUtterance === 'function'

let voicesCache = null

/** Усі доступні голоси (браузери заповнюють список асинхронно — кешуємо). */
export function getVoices() {
  if (!synth) return []
  const list = synth.getVoices?.() ?? []
  if (list.length) voicesCache = list
  return voicesCache ?? []
}

/** Голоси, схожі на українську, від найкращих. */
export function ukrainianVoices() {
  const uk = getVoices().filter((v) => /^uk(-|_|$)/i.test(v.lang))
  const localFirst = (a, b) => Number(b.localService) - Number(a.localService)
  return [...uk].sort((a, b) => {
    if (a.localService !== b.localService) return localFirst(a, b)
    const aMs = /microsoft|google/i.test(a.name) ? 1 : 0
    const bMs = /microsoft|google/i.test(b.name) ? 1 : 0
    if (aMs !== bMs) return bMs - aMs
    return a.name.localeCompare(b.name)
  })
}

/** Готовий голос за збереженим іменем, або перший український. */
export function pickVoice(preferredName) {
  const uk = ukrainianVoices()
  if (preferredName) {
    const found = uk.find((v) => v.name === preferredName)
    if (found) return found
  }
  return uk[0] ?? null
}

/**
 * Произносить текст системним голосом браузера.
 *
 * @param {string} text
 * @param {object} [opts]
 */
export function speak(text, opts = {}) {
  if (!isSupported()) {
    opts.onError?.()
    return null
  }

  const clean = stripStress(String(text ?? '')).trim()
  if (!clean) {
    opts.onError?.()
    return null
  }

  stop()

  const utter = new window.SpeechSynthesisUtterance(clean)
  utter.lang = opts.lang ?? 'uk-UA'
  utter.rate = clamp(opts.rate ?? 0.95, 0.1, 10)
  utter.pitch = clamp(opts.pitch ?? 1, 0, 2)
  utter.volume = clamp(opts.volume ?? 1, 0, 1)

  const voice = pickVoice(opts.voiceName)
  if (voice) {
    utter.voice = voice
    utter.lang = voice.lang
  }

  utter.onstart = () => opts.onStart?.()
  utter.onend = () => opts.onEnd?.()
  utter.onerror = (e) => {
    if (e?.error === 'interrupted' || e?.error === 'canceled') return
    opts.onError?.()
  }

  synth.speak(utter)
  return utter
}

export function stop() {
  if (!synth) return
  try {
    synth.cancel()
  } catch {
    /* ігноруємо */
  }
}

/** Говорить слова по черзі — зручно для наголосів. */
export function speakSequence(items, opts = {}) {
  if (!isSupported() || !items?.length) return () => {}

  stop()
  let cancelled = false

  items.forEach((text, i) => {
    if (cancelled) return
    const utter = new window.SpeechSynthesisUtterance(stripStress(text).trim())
    utter.lang = opts.lang ?? 'uk-UA'
    utter.rate = clamp(opts.rate ?? 0.9, 0.1, 10)
    const voice = pickVoice(opts.voiceName)
    if (voice) {
      utter.voice = voice
      utter.lang = voice.lang
    }
    if (i === 0) utter.onstart = () => opts.onStart?.()
    if (i === items.length - 1) utter.onend = () => opts.onEnd?.()
    synth.speak(utter)
  })

  return () => {
    cancelled = true
    stop()
  }
}

export const clamp = (v, lo, hi) => Math.min(Math.max(Number(v) || 0, lo), hi)

/** Chrome іноді не віддає список голосів одразу. */
export function onVoicesChanged(cb) {
  if (!synth?.addEventListener) return () => {}
  synth.addEventListener('voiceschanged', cb)
  return () => synth.removeEventListener('voiceschanged', cb)
}
